/**
 * Shared classification of FormManager / project_fields rows.
 * Keeps wizard (AdditionalInfoStep, buildFieldData) and legacy ContributionComp aligned.
 */

/** Types never shown in wizard "additional" step (AdditionalInfoStep). */
export const EXCLUDED_FROM_ADDITIONAL_TYPES = new Set([
  "address",
  "header",
  "file",
  "select",
]);

export function isStoryTextField(field) {
  return (
    String(field?.field_name || "")
      .toLowerCase()
      .includes("story") && String(field?.field_type || "").toLowerCase() !== "file"
  );
}

export function isCaptionOrSubtitleField(field) {
  const name = String(field?.field_name || "").toLowerCase();
  return name.includes("caption") || name.includes("subtitle");
}

export function getHeaderField(formFields) {
  const list = formFields || [];
  return list.find((f) => String(f?.field_type || "").toLowerCase() === "header");
}

export function getCaptionField(formFields) {
  const list = formFields || [];
  return list.find((f) => isCaptionOrSubtitleField(f));
}

/** First non-file story text field — matches wizard `storyText` binding in buildFieldData. */
export function getPrimaryStoryTextField(formFields) {
  const list = formFields || [];
  return list.find((f) => isStoryTextField(f));
}

export function getFileFields(formFields) {
  return (formFields || []).filter(
    (f) => String(f?.field_type || "").toLowerCase() === "file",
  );
}

/** Same filter as wizard AdditionalInfoStep (optional extras). */
export function getAdditionalFields(formFields) {
  return (formFields || []).filter(
    (f) =>
      !EXCLUDED_FROM_ADDITIONAL_TYPES.has(String(f?.field_type || "").toLowerCase()) &&
      !isStoryTextField(f) &&
      !isCaptionOrSubtitleField(f),
  );
}

/**
 * Build the EDRDynamic edit payload maps from the form's field defs and
 * the component's working {field_id, field_value} list.
 *
 * Returns BOTH:
 *   - fieldsById:   { [field_id]: value } — authoritative. field_ids are
 *                   unique, so this can never collide even when a form has
 *                   two fields sharing a field_name (the June-25 MKE
 *                   "Place Name" data-loss bug).
 *   - fieldsByName: { [field_name]: value } — legacy EDRDynamic.php
 *                   signature, kept for backward compatibility during the
 *                   deploy window. When field names collide this map
 *                   silently collapses them (last write wins) — which is
 *                   exactly the bug — so the server prefers fieldsById.
 *
 * Only fields present in `fieldData` are emitted, matching the prior
 * inline behavior (a field with no working entry is left untouched).
 */
export function buildEditFieldMaps(formFields, fieldData) {
  const byId = new Map(
    (fieldData || []).map((f) => [f.field_id, f.field_value ?? ""]),
  );
  const fieldsById = {};
  const fieldsByName = {};
  (formFields || []).forEach((field) => {
    if (!byId.has(field.field_id)) return;
    const value = byId.get(field.field_id) ?? "";
    fieldsById[field.field_id] = value;
    fieldsByName[field.field_name] = value;
  });
  return { fieldsById, fieldsByName };
}

/**
 * One row per form field for submitDynDataNU — same rules as wizard `buildFieldData`.
 * `additionalValues` is keyed by field_id for fields shown in AdditionalInfoStep only.
 */
export function buildFieldDataPayload(formFields, {
  address = "",
  placeName = "",
  caption = "",
  storyText = "",
  additionalValues = {},
}) {
  if (!formFields?.length) return [];
  return formFields.map((field) => {
    let value = "";
    const type = String(field.field_type || "").toLowerCase();
    const name = String(field.field_name || "").toLowerCase();
    if (type === "address") {
      value = address || "";
    } else if (type === "header") {
      value = placeName || "";
    } else if (name.includes("caption") || name.includes("subtitle")) {
      value = caption || "";
    } else if (name.includes("story") && type !== "file") {
      value = storyText || "";
    } else if (type === "file") {
      value = "";
    } else if (additionalValues[field.field_id] !== undefined) {
      value = additionalValues[field.field_id];
    }
    return { field_id: field.field_id, field_value: value };
  });
}
